import React from "react";
import { Cell, flexRender } from "@tanstack/react-table";
import { CellTooltipWrapper } from "./CellTooltipWrapper";
import { HighlightedText } from "./HighlightedText";
import { MagnitudeBar } from "./MagnitudeBar";
import { ColumnStats } from "./useTableInstance";
import styles from "../styles/ReactTable.scss";

type StickyColumnsInfo = {
  selectColumnWidth: number;
  firstColumnWidth: number;
  hasSelectColumn: boolean;
  hasStickyFirstColumn: boolean;
};

type TableCellProps<T> = {
  cell: Cell<T, unknown>;
  index: number;
  stickyColumnsInfo: StickyColumnsInfo;
  isTruncated: boolean;
  onMouseEnterOrMove: (e: React.MouseEvent<HTMLTableDataCellElement>) => void;
  getCellHighlightStatus?: (
    rowId: string,
    columnId: string
  ) => {
    isMatch: boolean;
    isCurrentMatch: boolean;
  };
  searchQuery?: string;
  columnStats?: Record<string, ColumnStats>;
};

type StickyPositionResult = {
  isSticky: boolean;
  stickyLeft?: number;
};

function calculateStickyPosition(
  isSelectColumn: boolean,
  isFirstDataCol: boolean,
  selectColumnWidth: number,
  hasSelectColumn: boolean
): StickyPositionResult {
  if (isSelectColumn) {
    return { isSticky: true, stickyLeft: 0 };
  }

  if (isFirstDataCol) {
    return {
      isSticky: true,
      stickyLeft: hasSelectColumn ? selectColumnWidth : 0,
    };
  }

  return { isSticky: false };
}

/**
 * Calculate bar style for center-diverging layout.
 * Zero is ALWAYS at 50% (center of cell).
 * Returns the CSS properties for positioning and sizing the bar,
 * plus whether it's a negative value.
 */
function calculateDivergingBarStyle(
  value: number,
  min: number,
  max: number
): { barStyle: React.CSSProperties; isNegative: boolean } {
  const NEGATIVE_COLOR = "#E53935";
  const POSITIVE_COLOR = "#1E88E5";

  if (value < 0 && min < 0) {
    // Negative: percentage of left half (0-50%)
    const barWidthPercent = (Math.abs(value) / Math.abs(min)) * 50;
    return {
      barStyle: {
        left: `${50 - barWidthPercent}%`,
        width: `${barWidthPercent}%`,
        backgroundColor: NEGATIVE_COLOR,
      },
      isNegative: true,
    };
  }

  if (value > 0 && max > 0) {
    // Positive: percentage of right half (0-50%)
    const barWidthPercent = (value / max) * 50;
    return {
      barStyle: {
        left: "50%",
        width: `${barWidthPercent}%`,
        backgroundColor: POSITIVE_COLOR,
      },
      isNegative: false,
    };
  }

  // Zero value - no bar
  return {
    barStyle: { width: 0 },
    isNegative: false,
  };
}

function isFirstDataColumn(
  index: number,
  hasStickyFirstColumn: boolean,
  hasSelectColumn: boolean
): boolean {
  if (!hasStickyFirstColumn) {
    return false;
  }

  return (hasSelectColumn && index === 1) || (!hasSelectColumn && index === 0);
}

/**
 * Recursively walks a React element tree and wraps any string children
 * with HighlightedText to enable substring highlighting while preserving
 * the original structure (links, spans, etc.)
 */
function highlightElement(
  element: React.ReactNode,
  searchQuery: string,
  isCurrentMatch: boolean,
  key?: string | number
): React.ReactNode {
  // Handle strings - wrap with HighlightedText
  if (typeof element === "string") {
    return (
      <HighlightedText
        key={key}
        text={element}
        searchQuery={searchQuery}
        isCurrentMatch={isCurrentMatch}
      />
    );
  }

  // Handle numbers - convert to string and highlight
  if (typeof element === "number") {
    return (
      <HighlightedText
        key={key}
        text={String(element)}
        searchQuery={searchQuery}
        isCurrentMatch={isCurrentMatch}
      />
    );
  }

  // Handle arrays - map over children
  if (Array.isArray(element)) {
    return element.map((child, i) =>
      highlightElement(child, searchQuery, isCurrentMatch, i)
    );
  }

  // Handle React elements
  if (React.isValidElement(element)) {
    const elementType = element.type;

    // DOM element (type is a string like "a", "div", "span")
    if (typeof elementType === "string") {
      const { children } = element.props as { children?: React.ReactNode };

      if (children === undefined || children === null) {
        return element;
      }

      return React.cloneElement(
        element,
        { ...element.props, key: key ?? element.key },
        React.Children.map(children, (child, i) =>
          highlightElement(child, searchQuery, isCurrentMatch, i)
        )
      );
    }

    // Function component - try to invoke it to get its rendered output
    if (typeof elementType === "function") {
      try {
        // Check if it's a class component (has prototype.isReactComponent)
        const isClassComponent =
          elementType.prototype && elementType.prototype.isReactComponent;

        if (!isClassComponent) {
          // It's a function component - invoke it with props
          // eslint-disable-next-line @typescript-eslint/ban-types
          const rendered = (elementType as Function)(element.props);
          return highlightElement(rendered, searchQuery, isCurrentMatch, key);
        }
      } catch {
        // If invoking fails, fall through and return element unchanged
      }
    }

    // Class component or failed invocation - return as-is
    return element;
  }

  // Handle null, undefined, booleans - return as-is
  return element;
}

export function TableCell<T>({
  cell,
  index,
  stickyColumnsInfo,
  isTruncated,
  onMouseEnterOrMove,
  getCellHighlightStatus = () => ({
    isMatch: false,
    isCurrentMatch: false,
  }),
  searchQuery = "",
  columnStats = {},
}: TableCellProps<T>) {
  const {
    selectColumnWidth,
    hasSelectColumn,
    hasStickyFirstColumn,
  } = stickyColumnsInfo;

  const isSelectColumn = cell.column.id === "select";
  const isFirstDataCol = isFirstDataColumn(
    index,
    hasStickyFirstColumn,
    hasSelectColumn
  );

  const { isSticky, stickyLeft } = calculateStickyPosition(
    isSelectColumn,
    isFirstDataCol,
    selectColumnWidth,
    hasSelectColumn
  );

  const highlightStatus = getCellHighlightStatus(cell.row.id, cell.column.id);

  const shouldHighlight = highlightStatus.isMatch && searchQuery;

  const cellClassName = [
    isSticky ? styles.stickyCell : undefined,
    highlightStatus.isMatch ? styles.searchMatch : undefined,
    highlightStatus.isCurrentMatch ? styles.currentSearchMatch : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  const cellStyle: React.CSSProperties = {
    width: cell.column.getSize(),
    ...(isSticky && stickyLeft !== undefined ? { left: stickyLeft } : {}),
  };

  const renderCellContent = () => {
    const columnDef = cell.column.columnDef;
    const cellContext = cell.getContext();
    const rawValue = cell.getValue();

    // Read numericPrecision from column meta (set by getColumnDisplayOptions)
    const numericPrecision = (columnDef.meta as any)?.numericPrecision as
      | number
      | undefined;

    // Format a number for display, applying numericPrecision if set
    const formatNumber = (value: number): string => {
      return numericPrecision != null
        ? value.toFixed(numericPrecision)
        : String(value);
    };

    // Check if this is a numeric value that should show a magnitude bar
    const colStats = columnStats[cell.column.id];
    const shouldShowMagnitudeBar =
      typeof rawValue === "number" &&
      !Number.isNaN(rawValue) &&
      colStats &&
      colStats.hasVariance;

    // If it's a number with variance, render with MagnitudeBar
    if (shouldShowMagnitudeBar) {
      const { barStyle, isNegative } = calculateDivergingBarStyle(
        rawValue,
        colStats.min,
        colStats.max
      );

      // A column that supplies its own cell renderer owns how its value reads;
      // the bar is decoration around that, not a replacement for it. The plain
      // -number branch below already defers this way — this one did not, so a
      // column pairing a numeric `accessorFn` (to sort by) with a `cell` (to
      // format with) silently rendered the raw accessor value instead, and only
      // when the column happened to have variance.
      const customCell = columnDef.cell
        ? flexRender(columnDef.cell, cellContext)
        : null;

      const displayValue = customCell ?? formatNumber(rawValue);

      // If we should highlight, we need to highlight the number inside
      // MagnitudeBar. Skipped when the column renders itself: the rendered
      // output is a node, and there is no text to run the matcher over.
      if (shouldHighlight && !customCell) {
        return (
          <div className={styles.magnitudeBarCell}>
            {(isNegative || rawValue > 0) && (
              <div className={styles.magnitudeBar} style={barStyle} />
            )}
            <span className={styles.magnitudeBarValue}>
              <HighlightedText
                text={displayValue as string}
                searchQuery={searchQuery}
                isCurrentMatch={highlightStatus.isCurrentMatch}
              />
            </span>
          </div>
        );
      }

      return (
        <MagnitudeBar
          value={rawValue}
          min={colStats.min}
          max={colStats.max}
          displayValue={displayValue}
        />
      );
    }

    // For plain numbers (no magnitude bar), apply numericPrecision formatting
    if (
      typeof rawValue === "number" &&
      !Number.isNaN(rawValue) &&
      numericPrecision != null &&
      !columnDef.cell // Only format if no custom cell renderer
    ) {
      const displayValue = formatNumber(rawValue);

      if (shouldHighlight) {
        return (
          <HighlightedText
            text={displayValue}
            searchQuery={searchQuery}
            isCurrentMatch={highlightStatus.isCurrentMatch}
          />
        );
      }

      return displayValue;
    }

    // Get the rendered content - either from custom cell renderer or default
    let renderedContent: React.ReactNode;

    if (typeof columnDef.cell === "function") {
      // Invoke the cell renderer function directly to get the actual elements.
      // `columnStats` is passed alongside the usual context so a renderer can
      // draw its value at a scale shared with the rest of the column -- the
      // same aggregate the magnitude bars use, and the only way a cell can
      // know anything about its neighbours.
      // Built as a variable rather than passed inline: an object literal at
      // the call site is "fresh", and TypeScript rejects the extra property
      // against ReactTable's CellContext. Widening the context type upstream
      // is not ours to do, and a cast would hide a real mismatch if that type
      // ever changes.
      const contextWithStats = { ...cellContext, columnStats: colStats };

      renderedContent = columnDef.cell(contextWithStats);
    } else {
      // No custom `cell` (see useTableInstance's `defaultColumn`, which
      // deliberately suppresses TanStack's own default renderer here so
      // `columnDef.cell` reliably means "this column renders itself").
      // Replicate that suppressed default: plain stringified value.
      renderedContent = rawValue == null ? null : String(rawValue);
    }

    // If we should highlight, recursively walk the rendered content
    // and wrap text nodes with HighlightedText
    if (shouldHighlight) {
      return highlightElement(
        renderedContent,
        searchQuery,
        highlightStatus.isCurrentMatch
      );
    }

    return renderedContent;
  };

  // The truncation tooltip shows the cell's RAW VALUE, which is only the right
  // thing to show when the cell is displaying that value as text and ran out of
  // room. A column that renders its value as something else entirely -- a
  // graphic, say -- opts out here, because for it the tooltip would reveal a
  // string the user never saw and never wanted to see.
  const suppressValueTooltip = Boolean(
    (cell.column.columnDef.meta as any)?.suppressValueTooltip
  );

  return (
    <CellTooltipWrapper
      cell={cell}
      shouldShow={isTruncated && !suppressValueTooltip}
      searchQuery={searchQuery}
      isCurrentMatch={highlightStatus.isCurrentMatch}
    >
      <td
        className={cellClassName || undefined}
        onMouseEnter={onMouseEnterOrMove}
        onMouseMove={onMouseEnterOrMove}
        style={cellStyle}
      >
        {renderCellContent()}
      </td>
    </CellTooltipWrapper>
  );
}
